import { useI18n } from "../i18n/LanguageContext";

/**
 * Extended footer with the full set of regulatory licences and registered
 * offices. Displayed only inside authenticated areas (PortalLayout). Mostly
 * informative — the labels are translated but the legal addresses, CRD
 * numbers and country names stay literal.
 */
export default function LicensesFooter() {
  const { t } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer
      className="mt-24 border-t border-[color:var(--br-line)] pt-12 pb-10"
      data-testid="portal-footer"
    >
      <div className="max-w-[1280px] mx-auto px-6 sm:px-8">
        {/* Header row */}
        <div className="flex flex-col gap-2 mb-10">
          <div
            className="font-mono-fin text-[0.62rem] tracking-[0.28em] uppercase"
            style={{ color: "var(--br-bronze)" }}
            data-testid="footer-licenses-overline"
          >
            {t("footer.licenses_title")}
          </div>
          <h2
            className="font-serif-display text-[1.55rem] sm:text-[1.85rem] leading-[1.15]"
            style={{ color: "var(--br-parchment)" }}
          >
            ABG <em style={{ color: "var(--br-bronze-soft)", fontStyle: "italic" }}>Sundal Collier</em>
            <span style={{ color: "var(--br-text-dim)", fontSize: "0.8em" }}> · Group of regulated firms</span>
          </h2>
        </div>

        {/* FINRA brokerage block */}
        <div
          className="br-card p-6 sm:p-7 mb-8"
          style={{
            background: "rgba(14,20,34,0.55)",
            borderLeft: "2px solid var(--br-bronze)",
          }}
          data-testid="footer-finra-card"
        >
          <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-start">
            <div>
              <div
                className="font-mono-fin text-[0.6rem] tracking-[0.24em] uppercase mb-2"
                style={{ color: "var(--br-bronze)" }}
              >
                {t("footer.broker_title")}
              </div>
              <div
                className="font-serif-display text-[1.15rem]"
                style={{ color: "var(--br-parchment)" }}
                data-testid="footer-broker-name"
              >
                ABG Sundal Collier Inc.
              </div>
              <div
                className="text-[0.78rem] mt-1"
                style={{ color: "var(--br-text-dim)" }}
              >
                Established in Delaware since 08/09/1991
              </div>
              <div
                className="font-mono-fin text-[0.7rem] tracking-[0.12em] mt-2"
                style={{ color: "var(--br-text)" }}
              >
                CRD#: 30605 · SEC#: 8-44768
              </div>
              <div
                className="text-[0.72rem] mt-3 leading-relaxed"
                style={{ color: "var(--br-text-dim)" }}
              >
                {t("footer.broker_subline")}
              </div>
            </div>
            <a
              href="https://files.brokercheck.finra.org/firm/firm_30605.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="br-btn-outline text-[0.7rem]"
              style={{ padding: "8px 14px", whiteSpace: "nowrap" }}
              data-testid="footer-finra-link"
            >
              {t("footer.finra_link")}
            </a>
          </div>

          <div
            className="mt-5 pt-5 border-t text-[0.78rem] leading-relaxed"
            style={{
              borderColor: "var(--br-line)",
              color: "var(--br-text)",
            }}
          >
            <div
              className="font-mono-fin text-[0.58rem] tracking-[0.24em] uppercase mb-1"
              style={{ color: "var(--br-bronze)" }}
            >
              {t("footer.main_address")}
            </div>
            <div>
              112 West 34th Street, Suite 18027
              <br />
              New York, NY 10120 — United States
            </div>
          </div>
        </div>

        {/* European investment firms grid */}
        <div
          className="grid gap-4 sm:gap-5 sm:grid-cols-2 mb-10"
          data-testid="footer-eu-firms"
        >
          <InvestmentFirmCard
            t={t}
            firm="ABG Sundal Collier AB"
            address="Regeringsgatan 25"
            city="111 53 Stockholm"
            country="Sweden"
            registry={{ label: t("footer.enrolment_no"), value: "5359" }}
            link="https://www.consob.it/web/area-pubblica/imprese-investimento-senza-succursale"
            linkLabel={t("footer.consob_link")}
            testid="footer-firm-ab"
          />
          <InvestmentFirmCard
            t={t}
            firm="ABG Sundal Collier ASA"
            address="Munkedamsveien 45"
            city="0250 Oslo"
            country="Norway"
            registry={{ label: t("footer.enrolment_no"), value: "1187" }}
            link="https://www.consob.it/web/area-pubblica/imprese-investimento-senza-succursale"
            linkLabel={t("footer.consob_link")}
            testid="footer-firm-asa"
          />
          <InvestmentFirmCard
            t={t}
            firm="ABG Sundal Collier Denmark"
            address="Göteborg Plads 1, 8th floor"
            city="DK-2150 Copenhagen"
            country="Denmark"
            registry={{ label: t("footer.finanstilsynet_id"), value: "FT00085885" }}
            link="https://www.finanstilsynet.no/en/finanstilsynets-registry/details/?id=116058"
            linkLabel={t("footer.finanstilsynet_link")}
            testid="footer-firm-denmark"
          />
          <InvestmentFirmCard
            t={t}
            firm="ABG Sundal Collier ASA Niederlassung"
            address="Schillerstraße 2"
            city="60313 Frankfurt am Main"
            country="Germany"
            registry={{ label: t("footer.finanstilsynet_id"), value: "FT00101112" }}
            link="https://www.finanstilsynet.no/en/finanstilsynets-registry/details/?id=188009"
            linkLabel={t("footer.finanstilsynet_link")}
            testid="footer-firm-niederlassung"
          />
        </div>

        {/* EU regulatory / marketing disclaimers */}
        <div
          className="br-card p-6 sm:p-7 mb-8"
          style={{
            background: "rgba(167,128,73,0.04)",
            borderLeft: "2px solid var(--br-bronze)",
          }}
          data-testid="footer-disclaimers"
        >
          <div
            className="font-mono-fin text-[0.6rem] tracking-[0.24em] uppercase mb-3"
            style={{ color: "var(--br-bronze)" }}
          >
            {t("disc.title")}
          </div>
          <ul
            className="text-[0.74rem] leading-relaxed space-y-1.5"
            style={{ color: "var(--br-text-dim)" }}
          >
            <li data-testid="footer-disc-past">· {t("disc.past")}</li>
            <li data-testid="footer-disc-risk">· {t("disc.risk")}</li>
            <li data-testid="footer-disc-mkt">· {t("disc.mkt")}</li>
          </ul>
        </div>

        {/* Legal note */}
        <p
          className="text-[0.7rem] leading-relaxed mb-6 max-w-[920px]"
          style={{ color: "var(--br-text-dim)" }}
          data-testid="footer-legal-note"
        >
          {t("footer.legal_note")}
        </p>

        {/* Copyright row */}
        <div
          className="pt-6 border-t flex flex-col sm:flex-row justify-between gap-3 text-[0.7rem] font-mono-fin tracking-[0.12em]"
          style={{
            borderColor: "var(--br-line)",
            color: "var(--br-text-dim)",
          }}
        >
          <span data-testid="footer-members-only">
            {t("footer.members_only", { year })}
          </span>
          <a
            href="mailto:info@borealisreserve.com"
            className="hover:text-[color:var(--br-bronze-soft)] transition-colors"
            style={{ color: "var(--br-text-dim)" }}
            data-testid="footer-info-email"
          >
            info@borealisreserve.com
          </a>
        </div>
      </div>
    </footer>
  );
}

function InvestmentFirmCard({ t, firm, address, city, country, registry, link, linkLabel, testid }) {
  return (
    <div
      className="br-card p-5 sm:p-6"
      data-testid={testid}
      style={{ background: "rgba(14,20,34,0.4)" }}
    >
      <div
        className="font-mono-fin text-[0.58rem] tracking-[0.24em] uppercase mb-2"
        style={{ color: "var(--br-bronze)" }}
      >
        {t("footer.investment_firm")}
      </div>
      <div
        className="font-serif-display text-[1.05rem] mb-3"
        style={{ color: "var(--br-parchment)" }}
      >
        {firm}
      </div>
      <dl
        className="text-[0.76rem] leading-relaxed space-y-1 mb-3"
        style={{ color: "var(--br-text)" }}
      >
        <div>
          <span style={{ color: "var(--br-text-dim)" }}>{t("footer.registered_office")} · </span>
          <span>{address}</span>
        </div>
        <div>
          <span style={{ color: "var(--br-text-dim)" }}>{t("footer.city")} · </span>
          <span>{city}</span>
        </div>
        <div>
          <span style={{ color: "var(--br-text-dim)" }}>{t("footer.country")} · </span>
          <span>{country}</span>
        </div>
        <div className="font-mono-fin pt-1">
          <span style={{ color: "var(--br-text-dim)" }}>{registry.label} · </span>
          <span style={{ color: "var(--br-bronze-soft)" }}>{registry.value}</span>
        </div>
      </dl>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono-fin text-[0.62rem] tracking-[0.2em] uppercase hover:text-[color:var(--br-parchment)] transition-colors"
        style={{ color: "var(--br-bronze)" }}
      >
        {linkLabel}
      </a>
    </div>
  );
}
